import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, MenuItem, Card, CardContent, CircularProgress, Backdrop
} from '@mui/material';
import { Add, Edit, Delete, ViewModule, ViewList, Email, Phone, Work, Visibility } from '@mui/icons-material';

const Staff: React.FC = () => {
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', role: 'Stylist', status: 'Active', specialties: [] as string[] });
  const [availableSpecialties] = useState([
    'Hair Cutting', 'Hair Coloring', 'Highlights', 'Balayage', 'Bridal Makeup', 'Evening Makeup',
    'Facial Treatments', 'Chemical Peels', 'Manicure', 'Pedicure', 'Gel Nails', 'Massage Therapy', 'Body Treatments'
  ]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [appointmentsDialogOpen, setAppointmentsDialogOpen] = useState(false);
  const [selectedStaffAppointments, setSelectedStaffAppointments] = useState<any[]>([]);
  const [selectedStaffName, setSelectedStaffName] = useState('');

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      console.log('Fetching staff from API...');
      const response = await fetch('https://salon.rockyroadsolutions.com/api/staff');
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Staff data received:', data);
        setStaff(data);
      } else {
        console.error('API response not ok:', response.status);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingStaff ? `https://salon.rockyroadsolutions.com/api/staff/${editingStaff.id}` : 'https://salon.rockyroadsolutions.com/api/staff';
      const method = editingStaff ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        fetchStaff();
        setIsFormOpen(false);
        setEditingStaff(null);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', role: 'Stylist', status: 'Active', specialties: [] });
      }
    } catch (error) {
      console.error('Error saving staff:', error);
    }
  };

  const handleEdit = (member: any) => {
    setEditingStaff(member);
    setFormData({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone,
      role: member.role,
      status: member.status,
      specialties: member.specialties || []
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await fetch(`https://salon.rockyroadsolutions.com/api/staff/${id}`, { method: 'DELETE' });
        fetchStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
      }
    }
  };

  const handleViewAppointments = async (member: any) => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/appointments');
      if (response.ok) {
        const appointments = await response.json();
        const staffAppointments = appointments.filter((apt: any) => 
          apt.primaryStaff?.firstName === member.firstName && apt.primaryStaff?.lastName === member.lastName
        );
        setSelectedStaffAppointments(staffAppointments);
        setSelectedStaffName(`${member.firstName} ${member.lastName}`);
        setAppointmentsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Stylists & Staff</Typography>
        <Box display="flex" gap={2}>
          <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}>
            <IconButton size="small" onClick={() => setViewMode('grid')} sx={{ color: viewMode === 'grid' ? '#E91E63' : '#666' }}>
              <ViewModule />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('list')} sx={{ color: viewMode === 'list' ? '#E91E63' : '#666' }}>
              <ViewList />
            </IconButton>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => {
            setEditingStaff(null);
            setFormData({ firstName: '', lastName: '', email: '', phone: '', role: 'Stylist', status: 'Active', specialties: [] });
            setIsFormOpen(true);
          }}>
            Add Staff
          </Button>
        </Box>
      </Box>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {staff.map((member) => (
            <Grid item xs={12} md={6} lg={4} key={member.id}>
              <Card sx={{
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: '1px solid #FF5722',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 25px rgba(255, 87, 34, 0.2)' }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF5722 0%, #D84315 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}>
                      {member.firstName?.[0]}{member.lastName?.[0]}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight={600}>
                        {member.firstName} {member.lastName}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Work fontSize="small" color="action" />
                        <Chip
                          label={member.role}
                          size="small"
                          sx={{ 
                            background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                            color: 'white',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                        {member.specialties?.slice(0, 2).map((specialty: string, index: number) => (
                          <Chip 
                            key={index} 
                            label={specialty} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 20,
                              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                              color: 'white'
                            }} 
                          />
                        ))}
                        {member.specialties?.length > 2 && (
                          <Chip 
                            label={`+${member.specialties.length - 2}`} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 20,
                              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                              color: 'white'
                            }} 
                          />
                        )}
                      </Box>
                    </Box>
                    <Chip label={member.status} color={member.status === 'Active' ? 'success' : 'default'} size="small" />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Email fontSize="small" /> {member.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" /> {member.phone}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => handleViewAppointments(member)} sx={{ borderColor: '#2196F3', color: '#2196F3' }}>Appointments</Button>
                    <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(member)} sx={{ borderColor: '#FF9800', color: '#FF9800' }}>Edit</Button>
                    <Button size="small" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(member.id)} sx={{ borderColor: '#F44336', color: '#F44336' }}>Delete</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {staff.map((member) => (
            <Card key={member.id} sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: '1px solid #FF5722',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: '0 4px 15px rgba(255, 87, 34, 0.15)' }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF5722 0%, #D84315 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      {member.firstName?.[0]}{member.lastName?.[0]}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body1" fontWeight={600}>
                      {member.firstName} {member.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      {member.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body2" color="text.secondary">
                      {member.phone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Box>
                      <Chip
                        label={member.role}
                        size="small"
                        sx={{ 
                          background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                          color: 'white',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          fontSize: '0.7rem',
                          mb: 0.5
                        }}
                      />
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {member.specialties?.slice(0, 2).map((specialty: string, index: number) => (
                          <Chip 
                            key={index} 
                            label={specialty} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 18,
                              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                              color: 'white'
                            }} 
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Chip label={member.status} color={member.status === 'Active' ? 'success' : 'default'} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box display="flex" gap={1}>
                      <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => handleViewAppointments(member)} sx={{ borderColor: '#2196F3', color: '#2196F3', minWidth: 'auto', px: 1 }}>View</Button>
                      <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(member)} sx={{ borderColor: '#FF9800', color: '#FF9800', minWidth: 'auto', px: 1 }}>Edit</Button>
                      <Button size="small" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(member.id)} sx={{ borderColor: '#F44336', color: '#F44336', minWidth: 'auto', px: 1 }}>Del</Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="First Name" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Last Name" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Role" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})}>
                <MenuItem value="stylist">Stylist</MenuItem>
                <MenuItem value="therapist">Therapist</MenuItem>
                <MenuItem value="receptionist">Receptionist</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Status" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Specialties"
                value={formData.specialties}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({...formData, specialties: typeof value === 'string' ? value.split(',') : value});
                }}
                SelectProps={{ 
                  multiple: true,
                  renderValue: (selected) => (selected as string[]).join(', ')
                }}
                helperText="Select multiple specialties"
              >
                {availableSpecialties.map((specialty) => (
                  <MenuItem key={specialty} value={specialty}>
                    {specialty}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingStaff ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Staff Appointments Dialog */}
      <Dialog open={appointmentsDialogOpen} onClose={() => setAppointmentsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Appointments for {selectedStaffName}</DialogTitle>
        <DialogContent>
          {selectedStaffAppointments.length === 0 ? (
            <Typography>No appointments found for this staff member.</Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {selectedStaffAppointments.map((appointment) => (
                <Card key={appointment.id} sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {new Date(appointment.appointmentDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2">
                        {appointment.startTime} - {appointment.endTime}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Client</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {appointment.client?.firstName} {appointment.client?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                      <Typography variant="h6" color="#4CAF50" fontWeight={600}>
                        ${(parseFloat(appointment.totalPrice) + parseFloat(appointment.tipAmount || 0)).toFixed(2)}
                      </Typography>
                      {parseFloat(appointment.tipAmount || 0) > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Service: ${parseFloat(appointment.totalPrice).toFixed(2)} + Tip: ${parseFloat(appointment.tipAmount).toFixed(2)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <Chip 
                        label={appointment.status} 
                        color={appointment.status === 'COMPLETED' ? 'success' : appointment.status === 'CANCELLED' ? 'error' : 'primary'} 
                        size="small" 
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppointmentsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Staff;
